import { registerLocaleData } from '@angular/common';
import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HttpClient } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DreambooksApiModule, Frontend } from '@dreambooks/api';
import { DreambooksRendererModule, PROJECT_PHOTOS_PROVIDER, RendererService } from '@dreambooks/renderer';

// Register locale data for Angular i18n pipes and @dreambooks/renderer.
import localePtPt from '@angular/common/locales/pt-PT';
import localePt from '@angular/common/locales/pt';
import localeEnGb from '@angular/common/locales/en-GB';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import localeNl from '@angular/common/locales/nl';
registerLocaleData(localePtPt, 'pt-PT');
registerLocaleData(localePtPt, 'pt_PT');
registerLocaleData(localePt, 'pt-BR');
registerLocaleData(localePt, 'pt_BR');
registerLocaleData(localeEnGb, 'en-GB');
registerLocaleData(localeEnGb, 'en_US');
registerLocaleData(localeEs, 'es-ES');
registerLocaleData(localeFr, 'fr-FR');
registerLocaleData(localeDe, 'de-DE');
registerLocaleData(localeDe, 'de_DE');
registerLocaleData(localeIt, 'it-IT');
registerLocaleData(localeIt, 'it_IT');
registerLocaleData(localeNl, 'nl-NL'); // BCP 47 alias for Angular/web formatting
registerLocaleData(localeNl, 'nl_NL'); // Canonical Express locale

import { guestRoutes } from './app-guest.routes';
import { CachedRendererService } from './features/editor/services/cached-renderer.service';

// Factory for ngx-translate loader (shared with the main app config).
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * Bootstrap providers for the GUEST-ONLY build.
 *
 * Trimmed from the main `appConfig`:
 *  - routes → `guestRoutes` (public share links only)
 *  - drops `CustomRouteReuseStrategy` (editor form-state reuse — not used here)
 *  - drops `ReactivePhotosDebugInterceptor` (dev debug only)
 *
 * Kept identical so guest preview renders exactly as it does in the full app:
 *  - DreambooksRendererModule + PROJECT_PHOTOS_PROVIDER
 *  - RendererService → CachedRendererService (image caching for the preview)
 *  - DreambooksApiModule + ngx-translate
 */
export const appGuestConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideBrowserGlobalErrorListeners(),
    provideRouter(guestRoutes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    importProvidersFrom(
      TranslateModule.forRoot({
        fallbackLang: 'pt_PT',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      }),
      DreambooksApiModule.forRoot({
        apiUrl: '/api',
        frontend: 'web' as Frontend,
        debug: false,
        billingCountryCode: 'PT',
        locale: 'pt_PT'
      }),
      DreambooksRendererModule.forRoot({
        assetsUrl: './assets/renderer',
        exportDpi: 254,
        placeholderImage: 'img_placeholder_unified.svg',
        placeholderColor: '#e1e1e1',
        pageThumbnailHeight: 80,
        allowOriginalPhotoUrl: false
      })
    ),
    PROJECT_PHOTOS_PROVIDER,
    { provide: RendererService, useClass: CachedRendererService }
  ]
};
